import { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { ClientSelectGroup } from '@/components/ClientSelectGroup';
import { SystemUserSecret } from '@/components/config/SystemUserSecret';
import { ToolPage } from '@/components/ui/ToolPage';
import { clientService } from '@/services/clientService';
import { nextgenBrokerService } from '@/services/nextgenBrokerService';
import type { Client } from '@/types';

export default function GetSystemUserSecret() {
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientName, setSelectedClientName] = useState(() => (searchParams.get('clientName') || '').trim());
  const [fetching, setFetching] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setClients(await clientService.getAllClients());
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'The client list did not load. Reload the page to try again.');
      }
    })();
  }, []);

  const client = clients.find((c) => c.name === selectedClientName);

  const onSelectClient = (name: string) => {
    setSelectedClientName(name);
    setSecret(null);
    setErr(null);
  };

  const onFetch = async () => {
    setErr(null);
    setSecret(null);
    try {
      setFetching(true);
      const res = await nextgenBrokerService.getSystemUserSecret(selectedClientName);
      setSecret(res.secretString || '');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'The account did not answer.');
    } finally {
      setFetching(false);
    }
  };

  return (
    <ToolPage
      title="System user password"
      subtitle="Read the system user's sign-in from a client's AWS account. Nothing in the account changes."
      width="narrow"
    >
      <Card>
        <Card.Body>
          {/* Enter in the client search must not send the form. */}
          <Form className="tool-form" onSubmit={(e) => e.preventDefault()}>
            <section className="tool-form__section">
              <h2 className="section-label">Client</h2>
              <ClientSelectGroup value={selectedClientName} onChange={onSelectClient} clients={clients} />
              {client && (
                <Form.Text className="d-block mt-2">
                  Account <span className="mono">{client.config.clientAccountId || 'not set'}</span> in{' '}
                  <span className="mono">{client.config.region || 'us-east-1'}</span>, from the client&apos;s config.
                </Form.Text>
              )}
            </section>

            <div className="tool-form__actions">
              <Button variant="primary" onClick={onFetch} disabled={!client || fetching}>
                {fetching ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Getting the password
                  </>
                ) : (
                  'Get password'
                )}
              </Button>
              {!client && <span className="tool-form__hint">Pick a client first.</span>}
            </div>
          </Form>
        </Card.Body>
      </Card>

      {err && (
        <Alert variant="danger" className="mt-3 mb-0">
          <strong>Could not get the password.</strong> {err}
        </Alert>
      )}

      {secret !== null && (
        <Card className="mt-3">
          <Card.Body>
            <h2 className="section-label">System user sign-in</h2>
            <SystemUserSecret raw={secret} />
          </Card.Body>
        </Card>
      )}
    </ToolPage>
  );
}
